import { useCallback, useEffect, useRef, useState } from 'react'
import type { TourStep } from './types'

interface UseWizardTourArgs {
  steps: TourStep[]
  onFinish?: () => void
  onSkip?: () => void
}

export interface WizardTourApi {
  isActive: boolean
  currentIndex: number
  currentStep: TourStep | undefined
  totalSteps: number
  start: () => void
  next: () => void
  back: () => void
  goTo: (index: number) => void
  skip: () => void
  finish: () => void
}

// Pure UI state machine for the AI-162 wizard coach-mark tour. Owns ONLY the
// current step index and the active/inactive transitions — no DOM, no portal,
// no measurement (WizardTour, U5, does the anchoring). `next()` on the last step
// finishes; `skip()`/`finish()` fire their callback exactly once and deactivate.
export const useWizardTour = ({
  steps,
  onFinish,
  onSkip,
}: UseWizardTourArgs): WizardTourApi => {
  const totalSteps = steps.length
  const [currentIndex, setCurrentIndex] = useState(0)
  const [isActive, setIsActive] = useState(false)

  // Mirror the latest values into refs so every transition callback can keep an
  // EMPTY dependency array (referentially stable — D6) while still reading the
  // current state. Writing a ref during render to cache current state is safe:
  // it is deterministic and recomputed every render, so it can never tear.
  const indexRef = useRef(currentIndex)
  indexRef.current = currentIndex
  const activeRef = useRef(isActive)
  activeRef.current = isActive
  const totalRef = useRef(totalSteps)
  totalRef.current = totalSteps

  // Keep the callbacks stable regardless of onFinish/onSkip identity (callers
  // commonly pass inline arrows), mirroring usePersonaPlayback's ref pattern.
  const onFinishRef = useRef(onFinish)
  const onSkipRef = useRef(onSkip)
  useEffect(() => {
    onFinishRef.current = onFinish
  }, [onFinish])
  useEffect(() => {
    onSkipRef.current = onSkip
  }, [onSkip])

  const start = useCallback(() => {
    activeRef.current = true
    setCurrentIndex(0)
    setIsActive(true)
  }, [])

  // Eagerly flip activeRef so a second synchronous finish()/skip() in the same
  // tick is guarded — guarantees the "exactly once" callback contract.
  const finish = useCallback(() => {
    if (!activeRef.current) return
    activeRef.current = false
    setIsActive(false)
    onFinishRef.current?.()
  }, [])

  const skip = useCallback(() => {
    if (!activeRef.current) return
    activeRef.current = false
    setIsActive(false)
    onSkipRef.current?.()
  }, [])

  const next = useCallback(() => {
    if (!activeRef.current) return
    if (indexRef.current >= totalRef.current - 1) {
      finish()
      return
    }
    setCurrentIndex(i => Math.min(totalRef.current - 1, i + 1))
  }, [finish])

  const back = useCallback(() => {
    if (!activeRef.current) return
    setCurrentIndex(i => Math.max(0, i - 1))
  }, [])

  const goTo = useCallback((index: number) => {
    if (!activeRef.current) return
    if (index < 0 || index >= totalRef.current) return
    setCurrentIndex(index)
  }, [])

  return {
    isActive,
    currentIndex,
    currentStep: steps[currentIndex],
    totalSteps,
    start,
    next,
    back,
    goTo,
    skip,
    finish,
  }
}
